import React, { useEffect, useRef, useState } from 'react';
import { Lock, Unlock, Clock, Link as LinkIcon, ClipboardList, Printer, Plus, Edit2, Trash2, X, Save, RotateCcw, ChevronUp, ChevronDown, Image as ImageIcon, Upload } from 'lucide-react';
import { Store, StoreOrderType } from '../../types';
import { updateStore, createStore, deleteStore, restoreStore, reorderStores } from '../../api/stores';
import { uploadImage } from '../../api/upload';
import {
  STORE_DAY_LABELS,
  STORE_HOUR_OPTIONS,
  STORE_OPERATION_DAY_OPTIONS,
  formatStoreOperationSummary,
  toggleOperationDay,
} from '../../utils/storeOperation';

interface StoresTabProps {
  onUpdateStore?: (store: Store) => void;
  stores: Store[];
  deletedStores?: Store[];
  storeMetrics?: Record<string, { orderCount: number; totalRevenue: number }>;
  onRefreshPage?: () => Promise<void> | void;
  onViewOrders: (storeId: string) => void;
  onPrint?: (storeId: string) => void;
  isAdmin?: boolean;
  sellers?: { id: string; username: string }[];
  sellerId?: string;
}

export const StoresTab: React.FC<StoresTabProps> = ({
  onUpdateStore: parentUpdateStore,
  stores: initialStores,
  deletedStores: initialDeletedStores = [],
  storeMetrics = {},
  onRefreshPage,
  onViewOrders,
  onPrint,
  isAdmin = false,
  sellers = [],
  sellerId,
}) => {
  const defaultOperationDays = [1, 2, 3, 4, 5];
  const [stores, setStores] = useState<Store[]>(initialStores || []);
  const [deletedStores, setDeletedStores] = useState<Store[]>(
    initialDeletedStores || [],
  );
  const [showDeleted, setShowDeleted] = useState(false);
  const [copiedStoreId, setCopiedStoreId] = useState<string | null>(null);

  // Creation state
  const [isAdding, setIsAdding] = useState(false);
  const [newStoreName, setNewStoreName] = useState('');
  const [newStoreSlug, setNewStoreSlug] = useState('');
  const [newStoreDescription, setNewStoreDescription] = useState('');
  const [newStoreType, setNewStoreType] = useState<StoreOrderType>('DELIVERY');
  const [newStoreDay, setNewStoreDay] = useState<number>(1);
  const [newStoreOperationDayOfWeeks, setNewStoreOperationDayOfWeeks] =
    useState<number[]>(defaultOperationDays);
  const [newStoreOperationStartHour, setNewStoreOperationStartHour] =
    useState<number>(9);
  const [newStoreOperationEndHour, setNewStoreOperationEndHour] =
    useState<number>(18);
  const [newStoreHasUnmannedWindow, setNewStoreHasUnmannedWindow] =
    useState(false);
  const [newStorePickupUnmannedStartHour, setNewStorePickupUnmannedStartHour] =
    useState<number>(20);
  const [newStorePickupUnmannedLabel, setNewStorePickupUnmannedLabel] =
    useState('');
  const [
    newStoreSupportsArrivalDateFilter,
    setNewStoreSupportsArrivalDateFilter,
  ] = useState(false);
  const [newStoreAcceptsPickupTime, setNewStoreAcceptsPickupTime] =
    useState(true);
  const [newStoreAcceptsRequestMessage, setNewStoreAcceptsRequestMessage] =
    useState(true);
  const [newStoreAcceptsOrderEdit, setNewStoreAcceptsOrderEdit] =
    useState(true);
  const [newStoreSellerId, setNewStoreSellerId] = useState<string>('');

  // Editing state
  const [editingStoreId, setEditingStoreId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editShareImage, setEditShareImage] = useState('');
  const [isUploadingShareImage, setIsUploadingShareImage] = useState(false);
  const shareImageInputRef = useRef<HTMLInputElement>(null);
  const [editType, setEditType] = useState<StoreOrderType>('DELIVERY');
  const [editDay, setEditDay] = useState<number>(1);
  const [editOperationDayOfWeeks, setEditOperationDayOfWeeks] = useState<
    number[]
  >(defaultOperationDays);
  const [editOperationStartHour, setEditOperationStartHour] =
    useState<number>(9);
  const [editOperationEndHour, setEditOperationEndHour] = useState<number>(18);
  const [editHasUnmannedWindow, setEditHasUnmannedWindow] = useState(false);
  const [editPickupUnmannedStartHour, setEditPickupUnmannedStartHour] =
    useState<number>(20);
  const [editPickupUnmannedLabel, setEditPickupUnmannedLabel] = useState('');
  const [editSupportsArrivalDateFilter, setEditSupportsArrivalDateFilter] =
    useState(false);
  const [editAcceptsPickupTime, setEditAcceptsPickupTime] = useState(true);
  const [editAcceptsRequestMessage, setEditAcceptsRequestMessage] =
    useState(true);
  const [editAcceptsOrderEdit, setEditAcceptsOrderEdit] = useState(true);
  const [memoEdits, setMemoEdits] = useState<Record<string, string>>({});

  const displayedStores = showDeleted ? (deletedStores || []) : (stores || []);
  const editingStore =
    stores.find((store) => store.id === editingStoreId) || null;
  const getStoreErrorMessage = (error: any, fallback: string) => {
    const message = error?.response?.data?.message;
    if (Array.isArray(message)) {
      return message.join('\n');
    }

    return message || fallback;
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      }
    } catch (err) {
      console.error('Copy failed', err);
      return false;
    }
  };

  const buildStoreLink = (store: Store) => {
    const identifier = store.slug?.trim() || store.id;
    return `${window.location.origin}/store/${encodeURIComponent(identifier)}`;
  };

  useEffect(() => {
    setStores(initialStores || []);
  }, [initialStores]);

  useEffect(() => {
    setDeletedStores(initialDeletedStores || []);
  }, [initialDeletedStores]);

  const resolveRecommendedUnmannedStartHour = (
    operationStartHour: number,
    operationEndHour: number,
    preferredHour = 20,
  ) => {
    const minHour = operationStartHour + 1;
    const maxHour = operationEndHour - 1;

    if (maxHour < minHour) {
      return minHour;
    }

    return Math.min(maxHour, Math.max(minHour, preferredHour));
  };

  const validateStoreOrderSettings = (
    orderType: StoreOrderType,
    operationStartHour: number,
    operationEndHour: number,
    hasUnmannedWindow: boolean,
    pickupUnmannedStartHour: number,
  ) => {
    if (orderType !== 'STORE') {
      return true;
    }

    if (operationStartHour >= operationEndHour) {
      alert('운영 시작 시간은 종료 시간보다 빨라야 합니다.');
      return false;
    }

    if (
      hasUnmannedWindow &&
      (pickupUnmannedStartHour <= operationStartHour ||
        pickupUnmannedStartHour >= operationEndHour)
    ) {
      alert('무인 전환 시간은 운영 시간 안쪽에서 설정해주세요.');
      return false;
    }

    return true;
  };

  const buildStoreTypePayload = (
    orderType: StoreOrderType,
    operationDayOfWeeks: number[],
    operationStartHour: number,
    operationEndHour: number,
    hasUnmannedWindow: boolean,
    pickupUnmannedStartHour: number,
    pickupUnmannedLabel: string,
    supportsArrivalDateFilter: boolean,
    acceptsPickupTime: boolean,
    acceptsRequestMessage: boolean,
  ) => {
    if (orderType !== 'STORE') {
      return {
        defaultDeliveryDay: undefined,
        operationDayOfWeeks: [],
        operationStartHour: undefined,
        operationEndHour: undefined,
        pickupUnmannedStartHour: null,
        pickupUnmannedLabel: null,
        supportsArrivalDateFilter: false,
        acceptsPickupTime: true,
        acceptsRequestMessage: true,
      };
    }

    return {
      operationDayOfWeeks,
      operationStartHour,
      operationEndHour,
      pickupUnmannedStartHour: hasUnmannedWindow
        ? pickupUnmannedStartHour
        : null,
      pickupUnmannedLabel: hasUnmannedWindow
        ? pickupUnmannedLabel.trim() || '무인 수령'
        : null,
      supportsArrivalDateFilter,
      acceptsPickupTime,
      acceptsRequestMessage,
    };
  };

  const handleCreateStore = async () => {
    if (!newStoreName.trim()) return;
    if (
      !validateStoreOrderSettings(
        newStoreType,
        newStoreOperationStartHour,
        newStoreOperationEndHour,
        newStoreHasUnmannedWindow,
        newStorePickupUnmannedStartHour,
      )
    ) {
      return;
    }

    try {
      await createStore(newStoreName.trim(), {
        slug: newStoreSlug.trim() || undefined,
        description: newStoreDescription.trim() || undefined,
        orderType: newStoreType,
        defaultDeliveryDay:
          newStoreType === 'DELIVERY' ? newStoreDay : undefined,
        ...buildStoreTypePayload(
          newStoreType,
          newStoreOperationDayOfWeeks,
          newStoreOperationStartHour,
          newStoreOperationEndHour,
          newStoreHasUnmannedWindow,
          newStorePickupUnmannedStartHour,
          newStorePickupUnmannedLabel,
          newStoreSupportsArrivalDateFilter,
          newStoreAcceptsPickupTime,
          newStoreAcceptsRequestMessage,
        ),
        sellerId: newStoreSellerId || undefined,
        acceptsOrderEdit: newStoreAcceptsOrderEdit,
      });
      setNewStoreName('');
      setNewStoreSlug('');
      setNewStoreDescription('');
      setNewStoreType('DELIVERY');
      setNewStoreDay(1);
      setNewStoreOperationDayOfWeeks(defaultOperationDays);
      setNewStoreOperationStartHour(9);
      setNewStoreOperationEndHour(18);
      setNewStoreHasUnmannedWindow(false);
      setNewStorePickupUnmannedStartHour(20);
      setNewStorePickupUnmannedLabel('');
      setNewStoreSupportsArrivalDateFilter(false);
      setNewStoreAcceptsPickupTime(true);
      setNewStoreAcceptsRequestMessage(true);
      setNewStoreAcceptsOrderEdit(true);
      setNewStoreSellerId('');
      setIsAdding(false);
      await onRefreshPage?.();
      alert('거점이 추가되었습니다.');
    } catch (e) {
      console.error(e);
      alert(getStoreErrorMessage(e, '거점 추가 실패'));
    }
  };

  const handleUpdateStore = async (s: Store) => {
    if (!editName.trim()) {
      alert('거점명을 입력해주세요.');
      return;
    }

    if (
      !validateStoreOrderSettings(
        editType,
        editOperationStartHour,
        editOperationEndHour,
        editHasUnmannedWindow,
        editPickupUnmannedStartHour,
      )
    ) {
      return;
    }

    try {
      await updateStore(s.id, {
        name: editName.trim(),
        slug: editSlug.trim() || null,
        description: editDescription.trim() || null,
        shareImage: editShareImage.trim() || null,
        orderType: editType,
        defaultDeliveryDay: editType === 'DELIVERY' ? editDay : undefined,
        ...buildStoreTypePayload(
          editType,
          editOperationDayOfWeeks,
          editOperationStartHour,
          editOperationEndHour,
          editHasUnmannedWindow,
          editPickupUnmannedStartHour,
          editPickupUnmannedLabel,
          editSupportsArrivalDateFilter,
          editAcceptsPickupTime,
          editAcceptsRequestMessage,
        ),
        acceptsOrderEdit: editAcceptsOrderEdit,
      });
      setEditingStoreId(null);
      await onRefreshPage?.();
    } catch (e) {
      console.error(e);
      alert(getStoreErrorMessage(e, '수정 실패'));
    }
  };

  const handleDeleteStore = async (id: string) => {
    if (!confirm('정말 이 거점을 삭제하시겠습니까? \n삭제된 거점은 [삭제된 거점 보기]에서 복구할 수 있습니다.')) return;
    try {
      await deleteStore(id);
      await onRefreshPage?.();
    } catch (e) {
      console.error(e);
      alert('삭제 실패');
    }
  };

  const handleRestoreStore = async (id: string) => {
    if (!confirm('이 거점을 복구하시겠습니까?')) return;
    try {
      await restoreStore(id);
      await onRefreshPage?.();
      alert('거점이 복구되었습니다.');
    } catch (e) {
      console.error(e);
      alert('복구 실패');
    }
  };

  const handleMoveStore = async (filteredIndex: number, direction: 'up' | 'down') => {
    const targetFilteredIndex = direction === 'up' ? filteredIndex - 1 : filteredIndex + 1;
    if (targetFilteredIndex < 0 || targetFilteredIndex >= displayedStores.length) return;

    const storeToMove = displayedStores[filteredIndex];
    const targetStore = displayedStores[targetFilteredIndex];

    const newStores = [...stores];
    const indexInMain = newStores.findIndex(s => s.id === storeToMove.id);
    const targetIndexInMain = newStores.findIndex(s => s.id === targetStore.id);

    if (indexInMain === -1 || targetIndexInMain === -1) return;

    // Swap in the main array
    [newStores[indexInMain], newStores[targetIndexInMain]] = [newStores[targetIndexInMain], newStores[indexInMain]];

    // Optimistically update UI
    setStores([...newStores]);

    try {
      await reorderStores(newStores.map(s => s.id));
    } catch (error) {
      console.error('Failed to reorder stores', error);
      alert('순서 변경에 실패했습니다.');
      await onRefreshPage?.();
    }
  };

  const closeEditModal = () => {
    setEditingStoreId(null);
  };

  const handleShareImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setIsUploadingShareImage(true);
      const { url } = await uploadImage(file);
      setEditShareImage(url);
    } catch (error) {
      console.error(error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setIsUploadingShareImage(false);
      if (shareImageInputRef.current) {
        shareImageInputRef.current.value = '';
      }
    }
  };

  const startEditing = (s: Store) => {
    const nextStartHour = s.operationStartHour ?? 9;
    const nextEndHour = s.operationEndHour ?? 18;
    setEditingStoreId(s.id);
    setEditName(s.name);
    setEditSlug(s.slug || '');
    setEditDescription(s.description || '');
    setEditShareImage(s.shareImage || '');
    setEditType(s.orderType || 'DELIVERY');
    setEditDay(s.defaultDeliveryDay ?? 1);
    setEditOperationDayOfWeeks(s.operationDayOfWeeks || defaultOperationDays);
    setEditOperationStartHour(nextStartHour);
    setEditOperationEndHour(nextEndHour);
    setEditHasUnmannedWindow(
      s.pickupUnmannedStartHour !== undefined && s.pickupUnmannedStartHour !== null,
    );
    setEditPickupUnmannedStartHour(
      resolveRecommendedUnmannedStartHour(
        nextStartHour,
        nextEndHour,
        s.pickupUnmannedStartHour ?? 20,
      ),
    );
    setEditPickupUnmannedLabel(s.pickupUnmannedLabel || '');
    setEditSupportsArrivalDateFilter(s.supportsArrivalDateFilter === true);
    setEditAcceptsPickupTime(s.acceptsPickupTime !== false);
    setEditAcceptsRequestMessage(s.acceptsRequestMessage !== false);
    setEditAcceptsOrderEdit(s.acceptsOrderEdit !== false);
  };

  const getOrderTypeLabel = (orderType?: StoreOrderType) =>
    orderType === 'STORE' ? '매장형' : '배송지형';

  const renderStoreOrderSettings = ({
    operationDayOfWeeks,
    setOperationDayOfWeeks,
    operationStartHour,
    setOperationStartHour,
    operationEndHour,
    setOperationEndHour,
    hasUnmannedWindow,
    setHasUnmannedWindow,
    pickupUnmannedStartHour,
    setPickupUnmannedStartHour,
    pickupUnmannedLabel,
    setPickupUnmannedLabel,
    supportsArrivalDateFilter,
    setSupportsArrivalDateFilter,
    acceptsPickupTime,
    setAcceptsPickupTime,
    acceptsRequestMessage,
    setAcceptsRequestMessage,
  }: {
    operationDayOfWeeks: number[];
    setOperationDayOfWeeks: React.Dispatch<React.SetStateAction<number[]>>;
    operationStartHour: number;
    setOperationStartHour: React.Dispatch<React.SetStateAction<number>>;
    operationEndHour: number;
    setOperationEndHour: React.Dispatch<React.SetStateAction<number>>;
    hasUnmannedWindow: boolean;
    setHasUnmannedWindow: React.Dispatch<React.SetStateAction<boolean>>;
    pickupUnmannedStartHour: number;
    setPickupUnmannedStartHour: React.Dispatch<React.SetStateAction<number>>;
    pickupUnmannedLabel: string;
    setPickupUnmannedLabel: React.Dispatch<React.SetStateAction<string>>;
    supportsArrivalDateFilter: boolean;
    setSupportsArrivalDateFilter: React.Dispatch<React.SetStateAction<boolean>>;
    acceptsPickupTime: boolean;
    setAcceptsPickupTime: React.Dispatch<React.SetStateAction<boolean>>;
    acceptsRequestMessage: boolean;
    setAcceptsRequestMessage: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    const unmannedStartOptions = STORE_HOUR_OPTIONS.filter(
      (option) =>
        option.value > operationStartHour && option.value < operationEndHour,
    );
    const canConfigureUnmannedWindow = unmannedStartOptions.length > 0;

    return (
      <>
        <div>
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">운영일자</label>
          <div className="grid grid-cols-4 gap-2">
            {STORE_OPERATION_DAY_OPTIONS.map((day) => {
              const isSelected = operationDayOfWeeks.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() =>
                    setOperationDayOfWeeks((prev) =>
                      toggleOperationDay(prev, day.value),
                    )
                  }
                  className={`h-10 rounded-2xl text-xs font-black border transition-all ${isSelected
                    ? 'bg-emerald-500 text-white border-emerald-500'
                    : 'bg-gray-50 text-gray-500 border-gray-200'
                    }`}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">운영시간</label>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wide ml-1">
                시작 시간
              </span>
              <select
                value={operationStartHour}
                onChange={e => {
                  const nextStartHour = Number(e.target.value);
                  const nextEndHour =
                    operationEndHour <= nextStartHour
                      ? Math.min(24, nextStartHour + 1)
                      : operationEndHour;
                  setOperationStartHour(nextStartHour);
                  if (operationEndHour <= nextStartHour) {
                    setOperationEndHour(nextEndHour);
                  }
                  if (nextEndHour - nextStartHour <= 1) {
                    setHasUnmannedWindow(false);
                  }
                  setPickupUnmannedStartHour((prev) =>
                    resolveRecommendedUnmannedStartHour(
                      nextStartHour,
                      nextEndHour,
                      prev,
                    ),
                  );
                }}
                className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                {STORE_HOUR_OPTIONS.filter((option) => option.value < 24).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wide ml-1">
                종료 시간
              </span>
              <select
                value={operationEndHour}
                onChange={e => {
                  const nextEndHour = Number(e.target.value);
                  setOperationEndHour(nextEndHour);
                  if (nextEndHour - operationStartHour <= 1) {
                    setHasUnmannedWindow(false);
                  }
                  setPickupUnmannedStartHour((prev) =>
                    resolveRecommendedUnmannedStartHour(
                      operationStartHour,
                      nextEndHour,
                      prev,
                    ),
                  );
                }}
                className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                {STORE_HOUR_OPTIONS.filter((option) => option.value > operationStartHour).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
          <label className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xs font-black text-gray-900">일부 시간 무인 수령</div>
              <div className="mt-1 text-[10px] font-bold text-gray-500">
                예: 12~19시는 유인, 20시 이후는 무인 수령으로 안내합니다.
              </div>
            </div>
            <input
              type="checkbox"
              checked={canConfigureUnmannedWindow ? hasUnmannedWindow : false}
              disabled={!canConfigureUnmannedWindow}
              onChange={e => {
                const nextChecked = e.target.checked;
                setHasUnmannedWindow(nextChecked);
                if (nextChecked) {
                  setPickupUnmannedStartHour((prev) =>
                    resolveRecommendedUnmannedStartHour(
                      operationStartHour,
                      operationEndHour,
                      prev,
                    ),
                  );
                }
              }}
              className="h-5 w-5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500 disabled:opacity-40"
            />
          </label>

          {!canConfigureUnmannedWindow && (
            <p className="text-[10px] font-bold text-gray-500">
              무인 전환을 쓰려면 운영 시간이 최소 2시간 이상이어야 합니다.
            </p>
          )}

          {canConfigureUnmannedWindow && hasUnmannedWindow && (
            <>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">
                  무인 전환 시간
                </label>
                <select
                  value={pickupUnmannedStartHour}
                  onChange={e =>
                    setPickupUnmannedStartHour(Number(e.target.value))
                  }
                  className="w-full h-11 px-4 bg-white border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
                >
                  {unmannedStartOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}부터 무인
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">
                  무인 안내 문구
                </label>
                <input
                  value={pickupUnmannedLabel}
                  onChange={e => setPickupUnmannedLabel(e.target.value)}
                  placeholder='비워두면 "무인 수령"으로 표시됩니다.'
                  className="w-full h-11 px-4 bg-white border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
                />
              </div>
            </>
          )}
        </div>

        <label className="flex items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="pr-4">
            <div className="text-xs font-black text-gray-900">
              상품 입고날짜 필터 사용
            </div>
            <div className="mt-1 text-[10px] font-bold text-gray-500">
              고객 주문 페이지에서 날짜 버튼으로 해당 일자 상품만 골라 볼 수 있습니다.
            </div>
          </div>
          <input
            type="checkbox"
            checked={supportsArrivalDateFilter}
            onChange={e => setSupportsArrivalDateFilter(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
          />
        </label>

        <div className="space-y-2">
          <label className="flex items-center justify-between rounded-2xl border px-4 py-3 bg-gray-50 border-gray-200">
            <div className="pr-4">
              <div className="text-xs font-black text-gray-900">고객 방문시간 받기</div>
              <div className="mt-1 text-[10px] font-bold text-gray-500">
                고객 주문서에서 방문 시간 선택 입력창을 보여줍니다.
              </div>
            </div>
            <input
              type="checkbox"
              checked={acceptsPickupTime}
              onChange={e => setAcceptsPickupTime(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
            />
          </label>
          <label className="flex items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="pr-4">
              <div className="text-xs font-black text-gray-900">고객 요청메시지 받기</div>
              <div className="mt-1 text-[10px] font-bold text-gray-500">
                고객이 추가 요청사항을 주문서에 남길 수 있습니다.
              </div>
            </div>
            <input
              type="checkbox"
              checked={acceptsRequestMessage}
              onChange={e => setAcceptsRequestMessage(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
            />
          </label>
        </div>
      </>
    );
  };

  const renderCustomerOrderEditSetting = ({
    acceptsOrderEdit,
    setAcceptsOrderEdit,
  }: {
    acceptsOrderEdit: boolean;
    setAcceptsOrderEdit: React.Dispatch<React.SetStateAction<boolean>>;
  }) => (
    <label className="flex items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
      <div className="pr-4">
        <div className="text-xs font-black text-gray-900">
          고객 주문 수정 허용
        </div>
        <div className="mt-1 text-[10px] font-bold text-gray-500">
          주문내역과 카트 화면에서 고객이 주문 상품과 요청사항을 다시 수정할 수 있습니다.
        </div>
      </div>
      <input
        type="checkbox"
        checked={acceptsOrderEdit}
        onChange={e => setAcceptsOrderEdit(e.target.checked)}
        className="h-5 w-5 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
      />
    </label>
  );

  return (
    <div className="space-y-4 pb-20">
      <div className="flex justify-between items-center px-1">
        <h2 className="text-lg font-black text-gray-900">거점 관리</h2>
        {isAdmin && (
          <button
            onClick={() => setShowDeleted(!showDeleted)}
            className={`text-[10px] font-black px-3 py-1.5 rounded-xl transition-all ${showDeleted ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
          >
            {showDeleted ? '활성 거점 보기' : '삭제된 거점 보기'}
          </button>
        )}
      </div>

      {/* Add Store Section */}
      {isAdmin && !showDeleted && !isAdding && (
        <button
          onClick={() => setIsAdding(true)}
          className="w-full py-4 bg-white border-2 border-dashed border-gray-200 rounded-3xl text-gray-400 font-black text-xs flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
        >
          <Plus size={16} /> 새 거점 추가
        </button>
      )}

      {isAdmin && !showDeleted && isAdding && (
        <div className="bg-white p-6 rounded-3xl border shadow-md space-y-4 animate-in fade-in slide-in-from-top-2">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-black text-gray-900">새 거점 정보</h3>
            <button onClick={() => setIsAdding(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">거점명</label>
              <input
                value={newStoreName}
                onChange={e => setNewStoreName(e.target.value)}
                placeholder="예: 강남점, 역삼 오피스"
                className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">짧은 링크</label>
              <div className="flex overflow-hidden rounded-2xl border bg-gray-50 focus-within:ring-2 ring-emerald-500/20 focus-within:border-emerald-500 transition-all">
                <span className="flex items-center px-4 text-[11px] font-bold text-gray-400 border-r border-gray-100">
                  /store/
                </span>
                <input
                  value={newStoreSlug}
                  onChange={e => setNewStoreSlug(e.target.value)}
                  placeholder="gangnam"
                  className="min-w-0 flex-1 h-11 px-4 bg-transparent text-xs font-bold outline-none"
                />
              </div>
              <p className="mt-1.5 px-1 text-[10px] font-bold text-gray-400">
                비워두면 거점명 기준으로 자동 생성됩니다.
              </p>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">상세 설명</label>
              <textarea
                value={newStoreDescription}
                onChange={e => setNewStoreDescription(e.target.value)}
                placeholder="고객에게 보여줄 매장 소개, 운영 특징, 주문 안내를 입력하세요"
                className="w-full min-h-[110px] px-4 py-3 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">거점 유형</label>
              <select
                value={newStoreType}
                onChange={e => setNewStoreType(e.target.value as StoreOrderType)}
                className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                <option value="DELIVERY">배송지형 주문</option>
                <option value="STORE">매장형 주문</option>
              </select>
            </div>
            {newStoreType === 'DELIVERY' ? (
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">기본 배송 요일</label>
                <select
                  value={newStoreDay}
                  onChange={e => setNewStoreDay(Number(e.target.value))}
                  className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
                >
                  {STORE_DAY_LABELS.map((day, idx) => (
                    <option key={idx} value={idx}>{day}요일</option>
                  ))}
                </select>
              </div>
            ) : (
              renderStoreOrderSettings({
                operationDayOfWeeks: newStoreOperationDayOfWeeks,
                setOperationDayOfWeeks: setNewStoreOperationDayOfWeeks,
                operationStartHour: newStoreOperationStartHour,
                setOperationStartHour: setNewStoreOperationStartHour,
                operationEndHour: newStoreOperationEndHour,
                setOperationEndHour: setNewStoreOperationEndHour,
                hasUnmannedWindow: newStoreHasUnmannedWindow,
                setHasUnmannedWindow: setNewStoreHasUnmannedWindow,
                pickupUnmannedStartHour: newStorePickupUnmannedStartHour,
                setPickupUnmannedStartHour: setNewStorePickupUnmannedStartHour,
                pickupUnmannedLabel: newStorePickupUnmannedLabel,
                setPickupUnmannedLabel: setNewStorePickupUnmannedLabel,
                supportsArrivalDateFilter: newStoreSupportsArrivalDateFilter,
                setSupportsArrivalDateFilter:
                  setNewStoreSupportsArrivalDateFilter,
                acceptsPickupTime: newStoreAcceptsPickupTime,
                setAcceptsPickupTime: setNewStoreAcceptsPickupTime,
                acceptsRequestMessage: newStoreAcceptsRequestMessage,
                setAcceptsRequestMessage: setNewStoreAcceptsRequestMessage,
              })
            )}
            {renderCustomerOrderEditSetting({
              acceptsOrderEdit: newStoreAcceptsOrderEdit,
              setAcceptsOrderEdit: setNewStoreAcceptsOrderEdit,
            })}
            {isAdmin && sellers.length > 0 && (
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">거점 담당 판매자</label>
                <select
                  value={newStoreSellerId}
                  onChange={e => setNewStoreSellerId(e.target.value)}
                  className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all appearance-none"
                >
                  <option value="">판매자 선택 안함 (본인 할당)</option>
                  {sellers.map(s => (
                    <option key={s.id} value={s.id}>{s.username}</option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={handleCreateStore}
              className="w-full h-12 bg-emerald-500 text-white rounded-2xl font-black text-xs shadow-lg shadow-emerald-100 hover:bg-emerald-600 transition-colors"
            >
              거점 등록하기
            </button>
          </div>
        </div>
      )}

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-[32px] border shadow-sm overflow-hidden min-h-[400px]">
        <table className="w-full table-fixed text-left border-collapse">
          <colgroup>
            <col style={{ width: '4rem' }} />
            <col style={{ width: '14rem' }} />
            <col style={{ width: '10rem' }} />
            <col style={{ width: '8rem' }} />
            <col />
            <col style={{ width: '8rem' }} />
            <col style={{ width: '12rem' }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter w-16 text-center">순서</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">거점명</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">운영 정보</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter w-32 text-center">상태</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter">운영 메모</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-right w-32">누적 데이터</th>
              <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-tighter text-center w-48">관리</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {displayedStores.map((s, index) => {
              const metric = storeMetrics[s.id] || {
                orderCount: 0,
                totalRevenue: 0,
              };

              return (
                <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-4">
                    {!showDeleted && (
                      <div className="flex flex-col items-center gap-0.5">
                        <button
                          disabled={index === 0}
                          onClick={() => handleMoveStore(index, 'up')}
                          className={`p-1 rounded-md transition-colors ${index === 0 ? 'text-gray-100' : 'text-gray-400 hover:bg-white hover:text-emerald-500 shadow-sm border border-transparent hover:border-emerald-100'}`}
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          disabled={index === (stores?.length || 0) - 1}
                          onClick={() => handleMoveStore(index, 'down')}
                          className={`p-1 rounded-md transition-colors ${index === (stores?.length || 0) - 1 ? 'text-gray-100' : 'text-gray-400 hover:bg-white hover:text-emerald-500 shadow-sm border border-transparent hover:border-emerald-100'}`}
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-black text-gray-900">{s.name}</span>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${s.orderType === 'STORE' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                          {getOrderTypeLabel(s.orderType)}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={async () => {
                            const url = buildStoreLink(s);
                            const success = await copyToClipboard(url);
                            if (success) {
                              setCopiedStoreId(s.id);
                              setTimeout(() => setCopiedStoreId(null), 2000);
                            }
                          }}
                          className={`px-2 py-0.5 rounded-lg transition-all flex items-center gap-1.5 border ${copiedStoreId === s.id ? 'text-emerald-600 border-emerald-100 bg-emerald-50' : 'text-gray-400 bg-gray-50 border-gray-100 hover:bg-gray-100'}`}
                        >
                          <LinkIcon size={10} />
                          <span className="text-[9px] font-black">{copiedStoreId === s.id ? '링크 복사됨' : '카톡 링크'}</span>
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-block max-w-full truncate text-[10px] font-black px-2 py-1 bg-gray-100 text-gray-600 rounded-lg whitespace-nowrap">
                      {formatStoreOperationSummary(s)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {!showDeleted && (
                      <button
                        onClick={async () => {
                          try {
                            const nextStatus = !s.isClosed;
                            await updateStore(s.id, { isClosed: nextStatus });
                            await onRefreshPage?.();
                          } catch (error) {
                            console.error("Failed to toggle store status", error);
                          }
                        }}
                        className={`px-3 py-1.5 rounded-full text-[10px] font-black flex items-center justify-center gap-1.5 transition-all w-full ${s.isClosed ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-emerald-50 text-emerald-500 hover:bg-emerald-100'}`}
                      >
                        {s.isClosed ? <Lock size={12} /> : <Unlock size={12} />}
                        <span>{s.isClosed ? '종료' : '영업중'}</span>
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {!showDeleted && (
                      <div className="flex items-center gap-2 bg-orange-50/50 p-2 rounded-xl border border-orange-100/50 group/memo">
                        <Clock size={12} className="text-orange-400 shrink-0" />
                        <input
                          className="flex-1 bg-transparent text-[11px] font-bold outline-none text-orange-900 placeholder:text-orange-200"
                          placeholder="운영 메모"
                          value={memoEdits[s.id] !== undefined ? memoEdits[s.id] : (s.deliverySchedule || '')}
                          onChange={(e) => setMemoEdits(prev => ({ ...prev, [s.id]: e.target.value }))}
                        />
                        {(memoEdits[s.id] !== undefined && memoEdits[s.id] !== (s.deliverySchedule || '')) && (
                          <button
                            onClick={async () => {
                              const newMemo = memoEdits[s.id];
                              const updated = { ...s, deliverySchedule: newMemo };
                              if (parentUpdateStore) {
                                await parentUpdateStore(updated);
                              } else {
                                await updateStore(s.id, {
                                  deliverySchedule: newMemo,
                                });
                              }
                              setMemoEdits(prev => {
                                const next = { ...prev };
                                delete next[s.id];
                                return next;
                              });
                              await onRefreshPage?.();
                            }}
                            className="p-1 bg-orange-100 text-orange-600 rounded-md"
                          >
                            <Save size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{metric.orderCount}건</p>
                      <p className="text-xs font-black text-emerald-600">{metric.totalRevenue.toLocaleString()}원</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-1.5">
                      {showDeleted ? (
                        <button
                          onClick={() => handleRestoreStore(s.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-black hover:bg-emerald-100 transition-all"
                        >
                          <RotateCcw size={12} /> 복구하기
                        </button>
                      ) : (
                        <>
                          <button onClick={() => onViewOrders(s.id)} className="p-2 text-gray-400 hover:text-gray-900 bg-gray-50 rounded-xl transition-all" title="주문내역"><ClipboardList size={16} /></button>
                          <button onClick={() => onPrint?.(s.id)} className="p-2 text-gray-400 hover:text-gray-900 bg-gray-50 rounded-xl transition-all" title="리포트"><Printer size={16} /></button>
                          <button
                            onClick={() => startEditing(s)}
                            className="flex items-center gap-1.5 px-3 py-2 text-gray-500 hover:text-emerald-600 bg-gray-50 rounded-xl transition-all"
                            title="설정"
                          >
                            <Edit2 size={14} />
                            <span className="text-[10px] font-black">설정</span>
                          </button>
                          {isAdmin && (
                            <button onClick={() => handleDeleteStore(s.id)} className="p-2 text-gray-400 hover:text-red-500 bg-gray-50 rounded-xl transition-all" title="삭제"><Trash2 size={16} /></button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {displayedStores.length === 0 && (
          <div className="py-20 text-center">
            <p className="text-gray-400 font-bold text-sm">거점 데이터가 없습니다.</p>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {displayedStores.map((s, index) => {
          const metric = storeMetrics[s.id] || {
            orderCount: 0,
            totalRevenue: 0,
          };

          return (
            <div key={s.id} className="bg-white p-5 rounded-[32px] border shadow-sm flex flex-col gap-4 transition-all">
              <div className="flex justify-between items-start gap-4">
                <div className="space-y-1.5 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-black text-gray-900">{s.name}</h3>
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-lg ${s.orderType === 'STORE' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                      {getOrderTypeLabel(s.orderType)}
                    </span>
                  </div>
                  <p className="text-[11px] font-bold text-gray-400 break-words">
                    {formatStoreOperationSummary(s)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        const url = buildStoreLink(s);
                        const success = await copyToClipboard(url);
                        if (success) {
                          setCopiedStoreId(s.id);
                          setTimeout(() => setCopiedStoreId(null), 2000);
                        }
                      }}
                      className={`px-2.5 py-1 rounded-xl transition-all flex items-center gap-1.5 border ${copiedStoreId === s.id ? 'text-emerald-600 border-emerald-100 bg-emerald-50' : 'text-gray-400 bg-gray-50 border-gray-100'}`}
                    >
                      <LinkIcon size={10} />
                      <span className="text-[9px] font-black">{copiedStoreId === s.id ? '복사됨' : '카톡링크'}</span>
                    </button>
                    {!showDeleted && (
                      <div className="flex gap-1 bg-gray-50 rounded-lg p-0.5">
                        <button
                          disabled={index === 0}
                          onClick={() => handleMoveStore(index, 'up')}
                          className="p-1 text-gray-400 disabled:opacity-20"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          disabled={index === (stores?.length || 0) - 1}
                          onClick={() => handleMoveStore(index, 'down')}
                          className="p-1 text-gray-400 disabled:opacity-20"
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  {showDeleted ? (
                    <button
                      onClick={() => handleRestoreStore(s.id)}
                      className="p-2 bg-emerald-50 text-emerald-600 rounded-xl"
                    >
                      <RotateCcw size={14} />
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => startEditing(s)}
                        className="flex items-center gap-1.5 px-3 py-2 text-gray-500 bg-gray-50 rounded-xl"
                      >
                        <Edit2 size={14} />
                        <span className="text-[10px] font-black">설정</span>
                      </button>
                      {isAdmin && (
                        <button onClick={() => handleDeleteStore(s.id)} className="p-2 text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                      )}
                    </div>
                  )}
                  {!showDeleted && (
                    <button
                      onClick={async () => {
                        try {
                          const nextStatus = !s.isClosed;
                          await updateStore(s.id, { isClosed: nextStatus });
                          await onRefreshPage?.();
                        } catch (error) {
                          console.error("Failed to toggle store status", error);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-black flex items-center gap-1.5 transition-all ${s.isClosed ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-500'}`}
                    >
                      {s.isClosed ? <Lock size={12} /> : <Unlock size={12} />}
                      <span>{s.isClosed ? '영업종료' : '영업중'}</span>
                    </button>
                  )}
                </div>
              </div>

              {!showDeleted && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 bg-orange-50/50 p-3 rounded-2xl border border-orange-100/50">
                    <Clock size={14} className="text-orange-400" />
                    <input
                      className="flex-1 bg-transparent text-[11px] font-black outline-none text-orange-900 placeholder:text-orange-200"
                      placeholder="운영 메모를 입력하세요"
                      value={memoEdits[s.id] !== undefined ? memoEdits[s.id] : (s.deliverySchedule || '')}
                      onChange={(e) => setMemoEdits(prev => ({ ...prev, [s.id]: e.target.value }))}
                    />
                    {(memoEdits[s.id] !== undefined && memoEdits[s.id] !== (s.deliverySchedule || '')) && (
                      <button
                        onClick={async () => {
                          const newMemo = memoEdits[s.id];
                          const updated = { ...s, deliverySchedule: newMemo };
                          if (parentUpdateStore) {
                            await parentUpdateStore(updated);
                          } else {
                            await updateStore(s.id, {
                              deliverySchedule: newMemo,
                            });
                          }
                          setMemoEdits(prev => {
                            const next = { ...prev };
                            delete next[s.id];
                            return next;
                          });
                          await onRefreshPage?.();
                        }}
                        className="p-1.5 bg-orange-100 text-orange-600 rounded-lg"
                      >
                        <Save size={14} />
                      </button>
                    )}
                  </div>

                  <div className="bg-gray-50/50 rounded-2xl p-4 flex justify-between items-center">
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">주문/매출</p>
                      <p className="text-xs font-black text-gray-900">{metric.orderCount}건 / <span className="text-emerald-600">{metric.totalRevenue.toLocaleString()}원</span></p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => onViewOrders(s.id)}
                        className="p-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all"
                      >
                        <ClipboardList size={14} />
                      </button>
                      <button
                        onClick={() => onPrint?.(s.id)}
                        className="p-2.5 bg-white border border-gray-100 text-gray-900 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
                      >
                        <Printer size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editingStore && (
        <div className="fixed inset-0 z-[7000] flex items-end justify-center p-0 sm:items-center sm:p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeEditModal}
          />
          <div className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white rounded-t-[32px] sm:rounded-[32px] border border-gray-100 shadow-2xl p-6 sm:p-7 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-black text-gray-900">거점 설정</h3>
                <p className="text-[11px] font-bold text-gray-400 mt-1">
                  {editingStore.name} 운영 정보를 수정합니다.
                </p>
              </div>
              <button
                onClick={closeEditModal}
                className="p-2 rounded-full bg-gray-50 text-gray-400 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">
                  거점명
                </label>
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">
                  짧은 링크
                </label>
                <div className="flex overflow-hidden rounded-2xl border bg-gray-50 focus-within:ring-2 ring-emerald-500/20 focus-within:border-emerald-500 transition-all">
                  <span className="flex items-center px-4 text-[11px] font-bold text-gray-400 border-r border-gray-100">
                    /store/
                  </span>
                  <input
                    value={editSlug}
                    onChange={e => setEditSlug(e.target.value)}
                    placeholder="gangnam"
                    className="min-w-0 flex-1 h-11 px-4 bg-transparent text-xs font-bold outline-none"
                  />
                </div>
                <p className="mt-1.5 px-1 text-[10px] font-bold text-gray-400">
                  고객 주문 링크: /store/{editSlug.trim() || editingStore.id}
                </p>
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">
                  상세 설명
                </label>
                <textarea
                  value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                  placeholder="고객이 링크를 열었을 때 참고할 매장 소개를 입력하세요"
                  className="w-full min-h-[120px] px-4 py-3 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">
                  공유 미리보기 이미지 (OG)
                </label>
                <input
                  ref={shareImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleShareImageUpload}
                />
                <div className="rounded-2xl border bg-gray-50 p-4 flex gap-4">
                  <div className="w-28 aspect-[1.91/1] rounded-xl overflow-hidden border bg-white flex items-center justify-center shrink-0">
                    {editShareImage ? (
                      <img src={editShareImage} alt="공유 미리보기 이미지" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={24} className="text-gray-300" />
                    )}
                  </div>
                  <div className="flex-1 flex flex-col justify-between gap-2">
                    <p className="text-[10px] font-bold text-gray-400 leading-relaxed">
                      카카오톡·SNS 링크 미리보기에 노출됩니다. 권장 1200×630.
                      미설정 시 판매자 대표 이미지가 사용됩니다.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => shareImageInputRef.current?.click()}
                        disabled={isUploadingShareImage}
                        className="h-10 px-4 rounded-xl bg-emerald-500 text-white text-xs font-black inline-flex items-center gap-2 disabled:opacity-50"
                      >
                        {isUploadingShareImage ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>업로드 중...</span>
                          </>
                        ) : (
                          <>
                            <Upload size={14} />
                            <span>이미지 업로드</span>
                          </>
                        )}
                      </button>
                      {editShareImage && (
                        <button
                          type="button"
                          onClick={() => setEditShareImage('')}
                          className="h-10 px-4 rounded-xl bg-white border text-gray-500 text-xs font-black inline-flex items-center gap-2"
                        >
                          <Trash2 size={14} />
                          <span>제거</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">
                  거점 유형
                </label>
                <select
                  value={editType}
                  onChange={e => setEditType(e.target.value as StoreOrderType)}
                  className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
                >
                  <option value="DELIVERY">배송지형 주문</option>
                  <option value="STORE">매장형 주문</option>
                </select>
              </div>

              {editType === 'DELIVERY' ? (
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 ml-1">
                    기본 배송 요일
                  </label>
                  <select
                    value={editDay}
                    onChange={e => setEditDay(Number(e.target.value))}
                    className="w-full h-11 px-4 bg-gray-50 border rounded-2xl text-xs font-bold outline-none focus:ring-2 ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  >
                    {STORE_DAY_LABELS.map((day, idx) => (
                      <option key={idx} value={idx}>{day}요일</option>
                    ))}
                  </select>
                </div>
              ) : (
                renderStoreOrderSettings({
                  operationDayOfWeeks: editOperationDayOfWeeks,
                  setOperationDayOfWeeks: setEditOperationDayOfWeeks,
                  operationStartHour: editOperationStartHour,
                  setOperationStartHour: setEditOperationStartHour,
                  operationEndHour: editOperationEndHour,
                  setOperationEndHour: setEditOperationEndHour,
                  hasUnmannedWindow: editHasUnmannedWindow,
                  setHasUnmannedWindow: setEditHasUnmannedWindow,
                  pickupUnmannedStartHour: editPickupUnmannedStartHour,
                  setPickupUnmannedStartHour: setEditPickupUnmannedStartHour,
                  pickupUnmannedLabel: editPickupUnmannedLabel,
                  setPickupUnmannedLabel: setEditPickupUnmannedLabel,
                  supportsArrivalDateFilter: editSupportsArrivalDateFilter,
                  setSupportsArrivalDateFilter:
                    setEditSupportsArrivalDateFilter,
                  acceptsPickupTime: editAcceptsPickupTime,
                  setAcceptsPickupTime: setEditAcceptsPickupTime,
                  acceptsRequestMessage: editAcceptsRequestMessage,
                  setAcceptsRequestMessage: setEditAcceptsRequestMessage,
                })
              )}
              {renderCustomerOrderEditSetting({
                acceptsOrderEdit: editAcceptsOrderEdit,
                setAcceptsOrderEdit: setEditAcceptsOrderEdit,
              })}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={closeEditModal}
                className="px-4 h-11 rounded-2xl bg-gray-100 text-gray-500 text-xs font-black"
              >
                취소
              </button>
              <button
                onClick={() => handleUpdateStore(editingStore)}
                className="px-5 h-11 rounded-2xl bg-emerald-500 text-white text-xs font-black shadow-lg shadow-emerald-100"
              >
                저장하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
